import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  ArrowLeft,
  Check,
  Upload,
  ExternalLink,
  RotateCcw,
  X,
  Sparkles,
  FileText,
  CircleCheck,
  CircleDashed,
  CircleAlert,
  Clock,
  Pen,
  Lock,
  Loader2,
  AlertTriangle,
  LogOut,
} from "lucide-react";
import { useOutletContext, useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { Input } from "@/components/ui/Input";
import { Textarea } from "@/components/ui/Textarea";

import { Card } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { ButtonShadcn as Button } from "@/components/ui/Button";
import CustomTooltip from "@/components/shared/CustomTooltip";
import AlertModal from "@/components/shared/AlertModal";
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/Modal";
import PermissionGuard from "@/permissions/PermissionGuard";
import ModuleUnavailableNotice from "@/permissions/ModuleUnavailableNotice";
import useModuleAccess from "@/hooks/useModuleAccess";
import { MODULES } from "@/permissions/modules";
import { PERMISSIONS } from "@/permissions/constant";
import { apiErrorMessageFormat } from "@/utils/formErrorUtils";

import { cn } from "@/lib/utils";
import { ComplianceService } from "@/services/complianceService";
import useComplianceLiveUpdates from "../hooks/useComplianceLiveUpdates";
import { getComplianceStatusMeta, COMPLIANCE_OFFICER_UPLOAD_ATTESTATION } from "../constant";
import { documentReasonSchema, zodErrorMessages, buildFieldValuesSchema, zodFieldErrors } from "../validation.schema";
import CandidateContactPopover from "../components/CandidateContactPopover";
import CandidateNameLink from "../components/CandidateNameLink";
import CandidateAuditLogModal from "./components/CandidateAuditLogModal";
import RejectCandidateModal from "./components/RejectCandidateModal";
import ReleaseCandidateModal from "./components/ReleaseCandidateModal";
import { SectionTabs } from "../../candidate-analysis/component/SectionTabs";

// Quick-fill chips for the reject / re-request note. Officers write the same
// handful of reasons constantly; clicking one fills the textarea, which stays
// editable. Frontend-only by design — see BACKEND_REQUIREMENTS Appendix B.
const PRESET_REASON_TAGS = ["Blurred / Unreadable Image", "Expired Document", "Name Mismatch", "Missing Signature", "Incomplete Document", "Wrong Document Category"];

// Said once, shown in three places — the lock line under the fields, the
// tooltip on each disabled control, and the note beside the review actions.
// A control the officer cannot press says why in the same words wherever they
// look, and those words match the server's own refusal
// (COMPLIANCE_DOCUMENT_AWAITING_CANDIDATE).
const AWAITING_CANDIDATE_MESSAGE = "Waiting for the candidate to re-upload this document.";

const StatusBadge = ({ status }) => {
  if (!status) return <span className="text-[12px] text-text-primary/50">Not uploaded</span>;
  const meta = getComplianceStatusMeta(status);
  return <Badge label={meta.label} variant="gradient" badgeStyle="text-[11px]" colorIndex={meta.colorIndex} className="border-[1px] rounded-[2rem]" />;
};

// Small inline confidence chip for an AI-extracted field. The raw percentage is
// left out: the officer's decision is "verify this or not", which the level
// already answers, and a precise-looking score invites false confidence.
const ConfidenceChip = ({ level }) => {
  if (!level) return null;
  const cls =
    level === "high" ? "text-tone-emerald bg-tone-emerald-soft" : level === "medium" ? "text-tone-amber bg-tone-amber-soft" : "text-tone-rose bg-tone-rose-soft";
  return <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-medium ${cls}`}>{level}</span>;
};

// Anything the AI is not confident about must be eyeballed before approval.
const needsVerification = (extracted) => !!extracted && (extracted.confidenceLevel === "medium" || extracted.confidenceLevel === "low");

// A tiny status glyph for the left-rail checklist items.
const StatusGlyph = ({ status }) => {
  const common = "shrink-0";
  if (status === "approved") return <CircleCheck size={15} className={`${common} text-tone-emerald`} />;
  if (status === "rejected") return <CircleAlert size={15} className={`${common} text-tone-rose`} />;
  if (status === "re_requested") return <RotateCcw size={15} className={`${common} text-tone-purple`} />;
  if (status === "pending_for_review") return <Clock size={15} className={`${common} text-tone-amber`} />;
  return <CircleDashed size={15} className={`${common} text-text-primary/30`} />;
};

const isImage = (fmt) => ["jpg", "jpeg", "png", "webp", "gif"].includes((fmt || "").toLowerCase());

// ButtonShadcn's built-in variant colors (outline/default/secondary) are pinned to
// the static shadcn --background/--foreground/--muted/--primary-foreground vars,
// which this app's light/dark switcher never touches (it only drives bg-button-*,
// text-text-*, border-border-* — see themes/styles/{light,dark}.js). Overriding
// with those tokens here (twMerge drops the stale variant classes) keeps every
// button on this page legible in both themes.
const OUTLINE_BTN = "border-border-primary bg-transparent text-text-primary hover:bg-background-icon/60";
const PRIMARY_BTN = "bg-button-primary text-text-invert hover:bg-button-primary/85";
const SECONDARY_BTN = "bg-button-secondary text-text-base hover:bg-button-secondary/85";

// One geometry for every action button on this page (viewer header and review
// decisions alike), so Approve/Reject and History/Open/Replace line up instead
// of each carrying whatever its own variant defaults to.
const ACTION_BTN = "h-8 px-3.5 rounded-lg text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed";

const ComplianceDetail = () => {
  const setHeader = useOutletContext();
  const navigate = useNavigate();
  const { id } = useParams();

  // Read before the early returns below, as every hook must be.
  const { isAvailable: complianceAvailable } = useModuleAccess(MODULES.COMPLIANCE);

  const [candidate, setCandidate] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyDocId, setBusyDocId] = useState(null);

  // Which checklist subsection is currently focused in the viewer/panel.
  const [selectedSectionId, setSelectedSectionId] = useState(null);
  const [selectedSubId, setSelectedSubId] = useState(null);

  // Object URL for the document currently shown in the center viewer.
  const [viewer, setViewer] = useState({ docId: null, url: null, type: null });
  const [viewerLoading, setViewerLoading] = useState(false);

  // Field-confirmation inputs, keyed by documentId → { fieldName: value }.
  const [fieldValues, setFieldValues] = useState({});

  // Validation errors for the currently-edited document's fields, keyed by
  // fieldName. Cleared whenever the value changes or a save succeeds.
  const [fieldErrors, setFieldErrors] = useState({});

  // Whether the extracted-field inputs for the selected document are currently
  // editable. Saving fields (with confirmation) locks them; Edit re-opens.
  // Reset per selected document — approved/rejected docs are always locked.
  const [fieldsEditing, setFieldsEditing] = useState(false);

  // Confirmation dialogs — Save fields + Approve carry "seriousness" (they
  // change what an officer can no longer edit).
  const [saveFieldsConfirmOpen, setSaveFieldsConfirmOpen] = useState(false);
  const [approveConfirmOpen, setApproveConfirmOpen] = useState(false);

  // Candidate-level rejection (Phase 7C).
  const [rejectOpen, setRejectOpen] = useState(false);
  const [releaseOpen, setReleaseOpen] = useState(false);
  // Upload attestation (Phase 8C): { subsectionId, documentCategory } while the
  // dialog is open, null otherwise. Shown BEFORE the file picker — asking
  // someone to choose a file and then refusing the upload wastes their time,
  // and on the server the attestation gate runs before anything is read.
  const [attestation, setAttestation] = useState(null);
  const [attestationChecked, setAttestationChecked] = useState(false);
  // Reason modal (re-request; reject moved to the candidate level).
  const [reason, setReason] = useState({ open: false, mode: null, documentId: null, text: "" });

  // Document audit-history drawer.
  const [historyOpen, setHistoryOpen] = useState(false);

  // Upload: a single hidden input, target subsection tracked in a ref.
  const fileInputRef = useRef(null);
  const uploadTargetRef = useRef({ subsectionId: null, documentCategory: null });

  const fetchAll = useCallback(async () => {
    if (!id) return;
    try {
      const [detailRes, progressRes] = await Promise.all([ComplianceService.getCandidateDetail(id), ComplianceService.getCandidateProgress(id)]);
      const nextCandidate = detailRes?.candidate ?? null;
      setCandidate(nextCandidate);
      setProgress(progressRes ?? null);

      // Pre-fill the confirm inputs from AI-extracted values.
      // Officer edits already in state win over freshly-fetched values.
      const prefill = {};
      (nextCandidate?.documents ?? []).forEach((d) => {
        (d.extractedFields ?? []).forEach((f) => {
          if (!prefill[d.id]) prefill[d.id] = {};
          prefill[d.id][f.fieldName] = f.extractedValue ?? "";
        });
      });
      setFieldValues((prev) => {
        const merged = { ...prefill };
        Object.keys(prev).forEach((docId) => {
          merged[docId] = { ...(prefill[docId] ?? {}), ...prev[docId] };
        });
        return merged;
      });
    } catch (e) {
      console.error(e);
      toast.error(apiErrorMessageFormat(e, "Failed to load candidate"));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Live updates, scoped to THIS candidate. A tenant-broadcast event about
  // someone else must not refetch this screen — the officer may be mid-edit,
  // and a pointless refetch costs two requests. Extraction events are the
  // reason this matters most: they land while the officer is watching the
  // fields panel, and `fetchAll` merges officer edits over fetched values, so
  // a refetch fills in extracted fields without discarding typing in progress.
  useComplianceLiveUpdates({
    // The envelope identifies its own resource: a document event carries the
    // owning candidate in `parent`, a candidate event in `id`. Neither is
    // the camelCase REST shape this screen otherwise reads.
    filter: (envelope) => {
      // Config events belong to the tenant, not a candidate, but change
      // what this screen renders — a checklist edit moves the slots and
      // weights, and a settings save recomputes every candidate's status
      // server-side. So they refresh unconditionally; everything else
      // must match.
      const resource = envelope?.resource;
      if (resource === "compliance_checklist" || resource === "compliance_settings") {
        return true;
      }
      const own = envelope?.parent?.compliance_candidate ?? envelope?.id;
      return own === id;
    },
    onRefresh: fetchAll,
  });

  useEffect(() => {
    setHeader({
      title: "Compliance Review",
      subTitle: "",
    });
    return () => setHeader({ title: "", subTitle: "" });
  }, [setHeader]);

  // Map active documents by subsection for quick lookup.
  const docsBySub = useMemo(() => {
    const m = {};
    (candidate?.documents ?? []).forEach((d) => {
      if (d.subsectionId) m[d.subsectionId] = d;
    });
    return m;
  }, [candidate]);

  const sections = candidate?.checklist?.sections ?? [];

  // Documents offered in the reject modal (Phase 7C), each with a human label.
  // ComplianceDocumentOut carries no display name — only `documentCategory`
  // (a slug) and `originalFilename` — so the subsection name is resolved here,
  // exactly as the rest of this screen labels a document. Active documents
  // only: a superseded version is not a thing to reject, its replacement is.
  const rejectableDocuments = useMemo(() => {
    const subNameById = {};
    (candidate?.checklist?.sections ?? []).forEach((sec) =>
      (sec.subsections ?? []).forEach((sub) => {
        subNameById[sub.id] = sub.name;
      }),
    );
    const prettify = (slug) =>
      String(slug ?? "")
        .split(/[_\-\s]+/)
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
    return (candidate?.documents ?? [])
      .filter((d) => d.isActive !== false)
      .map((d) => ({
        id: d.id,
        status: d.status,
        name: subNameById[d.subsectionId] || prettify(d.documentCategory) || d.originalFilename || "Document",
      }));
  }, [candidate]);

  // Flattened list of subsections (in display order) for defaulting selection.
  const allSubsections = useMemo(() => sections.flatMap((s) => s.subsections.map((sub) => ({ ...sub, sectionName: s.name }))), [sections]);

  useEffect(() => {
    if (!sections.length) return;

    if (!selectedSectionId) {
      const firstSection = sections[0];
      setSelectedSectionId(firstSection.id);

      const firstSub = firstSection.subsections.find((s) => docsBySub[s.id]) ?? firstSection.subsections[0];

      if (firstSub) setSelectedSubId(firstSub.id);
    }
  }, [sections, docsBySub, selectedSectionId]);

  const selectedSub = allSubsections.find((s) => s.id === selectedSubId) ?? null;
  const selectedDoc = selectedSub ? docsBySub[selectedSub.id] : null;

  const currentSection = sections.find((s) => s.id === selectedSectionId) ?? null;

  const changeSection = (sectionId) => {
    setSelectedSectionId(sectionId);

    const sec = sections.find((s) => s.id === sectionId);

    if (!sec) return;

    const firstSub = sec.subsections.find((s) => docsBySub[s.id]) ?? sec.subsections[0];

    setSelectedSubId(firstSub?.id);
  };

  // A document that has been approved or rejected is a settled decision — its
  // fields lock and the Save/Edit controls disappear (only Re-request remains).
  const decided = !!selectedDoc && (selectedDoc.status === "approved" || selectedDoc.status === "rejected");

  // The candidate has been asked to replace this document and has not done so
  // yet, so the turn is theirs: every officer write waits for the replacement.
  // Approving the file just called wrong, saving fields read out of it, or
  // re-requesting it again are all decisions about a document on its way out —
  // a re-upload arrives as a NEW row, so none of them survives it. The second
  // re-request is the one that corrupted data: each opens its own log row and
  // a re-upload closes only the newest, leaving the stuck worklists reading a
  // row nobody can close.
  //
  // The document itself stays exactly where it was — visible, open-able,
  // downloadable, with its extracted values on screen. This withholds the
  // decision, not the evidence. The mirror of `canUpload` in the candidate
  // portal, read from the officer's side; the routes refuse these too.
  const awaitingCandidate = selectedDoc?.status === "re_requested";

  // The same question for the document the reason modal is open against, which
  // is not always the selected one — a re-request is reachable from there too.
  const reasonDocAwaitingCandidate =
    reason.mode === "rerequest" && (candidate?.documents ?? []).some((d) => d.id === reason.documentId && d.status === "re_requested");

  // When the selected document changes, decide the initial edit state: a
  // fresh, undecided document with nothing confirmed yet opens in edit mode so
  // the officer can review and save; anything already confirmed (or decided)
  // starts locked, with an Edit button to re-open it. A re-requested document
  // never auto-opens — it has no Save to reach, so opening it would drop the
  // officer straight into editing they cannot finish.
  useEffect(() => {
    if (!selectedDoc) {
      setFieldsEditing(false);
      return;
    }
    const isDecided = selectedDoc.status === "approved" || selectedDoc.status === "rejected";
    const isAwaitingCandidate = selectedDoc.status === "re_requested";
    setFieldsEditing(!isDecided && !isAwaitingCandidate && (selectedDoc.confirmedFieldCount ?? 0) === 0);
    setFieldErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDoc?.id, selectedDoc?.status]);

  // Load the selected document into the viewer (blob → object URL).
  useEffect(() => {
    let revoked = false;
    let createdUrl = null;
    const load = async () => {
      if (!selectedDoc) {
        setViewer({ docId: null, url: null, type: null });
        return;
      }
      if (viewer.docId === selectedDoc.id && viewer.url) return; // already loaded
      setViewerLoading(true);
      try {
        const response = await ComplianceService.fetchDocumentFile(selectedDoc.id);
        const type = response.headers?.["content-type"] || "application/octet-stream";
        const blob = new Blob([response.data], { type });
        createdUrl = URL.createObjectURL(blob);
        if (!revoked) setViewer({ docId: selectedDoc.id, url: createdUrl, type });
      } catch (err) {
        console.error(err);
        if (!revoked) setViewer({ docId: selectedDoc.id, url: null, type: null });
      } finally {
        if (!revoked) setViewerLoading(false);
      }
    };
    load();
    return () => {
      revoked = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDoc?.id]);

  // ── actions ─────────────────────────────────────────────────────────────
  const triggerUpload = (subsectionId, documentCategory) => {
    // Attestation gate (Phase 8C) — the file picker opens only after the
    // officer accepts.
    setAttestationChecked(false);
    setAttestation({ subsectionId, documentCategory });
  };

  const confirmAttestation = () => {
    if (!attestationChecked || !attestation) return;
    uploadTargetRef.current = {
      subsectionId: attestation.subsectionId,
      documentCategory: attestation.documentCategory,
    };
    setAttestation(null);
    fileInputRef.current?.click();
  };

  const onFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file later
    if (!file) return;
    const { subsectionId, documentCategory } = uploadTargetRef.current;
    setBusyDocId("upload");
    try {
      await ComplianceService.uploadDocument(id, {
        file,
        subsectionId,
        documentCategory,
        // Reaching here means confirmAttestation() ran — the picker is
        // only opened from there.
        attestationAccepted: true,
      });
      // Phase 8A: extraction runs after the response, so the fields are
      // not there yet. Say so rather than letting an empty fields panel
      // read as a failure.
      toast.success("Document uploaded — extracting fields…");
      if (subsectionId) setSelectedSubId(subsectionId);
      await fetchAll();
    } catch (err) {
      console.error(err);
      toast.error(apiErrorMessageFormat(err, "Upload failed"));
    } finally {
      setBusyDocId(null);
    }
  };

  const openInNewTab = () => {
    if (viewer.url) window.open(viewer.url, "_blank", "noopener");
  };

  const approve = async (documentId) => {
    setBusyDocId(documentId);
    try {
      await ComplianceService.approveDocument(documentId);
      toast.success("Document approved");
      await fetchAll();
    } catch (err) {
      console.error(err);
      toast.error(apiErrorMessageFormat(err, "Approve failed"));
    } finally {
      setBusyDocId(null);
    }
  };

  const submitReason = async () => {
    const { mode, documentId, text } = reason;

    const parsed = documentReasonSchema.safeParse({ mode, text });
    if (!parsed.success) {
      toast.error(zodErrorMessages(parsed.error)[0] ?? "Please check the reason.");
      return;
    }

    setBusyDocId(documentId);
    try {
      if (mode === "reject") {
        await ComplianceService.rejectDocument(documentId, text.trim() || null);
        toast.success("Document rejected");
      } else {
        await ComplianceService.rerequestDocument(documentId, text.trim());
        toast.success("Re-request sent");
      }
      setReason({ open: false, mode: null, documentId: null, text: "" });
      await fetchAll();
    } catch (err) {
      console.error(err);
      toast.error(apiErrorMessageFormat(err, "Action failed"));
    } finally {
      setBusyDocId(null);
    }
  };

  // Required extracted fields (per the checklist item's field config) must
  // carry a non-empty value before an officer can save — otherwise the
  // confirmation step is a no-op and an unverified/blank value could reach
  // Approve. Returns true when the current values pass.
  const validateFields = (documentId, requiredFields) => {
    const schema = buildFieldValuesSchema(requiredFields);
    const result = schema.safeParse(fieldValues[documentId] ?? {});
    if (!result.success) {
      setFieldErrors(zodFieldErrors(result.error));
      toast.error("Please fill in all required fields before saving.");
      return false;
    }
    setFieldErrors({});
    return true;
  };

  const handleSaveFieldsClick = () => {
    if (!selectedDoc || !selectedSub) return;
    if (!validateFields(selectedDoc.id, selectedSub.requiredFields)) return;
    setSaveFieldsConfirmOpen(true);
  };

  const saveFields = async (documentId, requiredFields) => {
    if (!validateFields(documentId, requiredFields)) return;
    const values = fieldValues[documentId] ?? {};
    const fields = (requiredFields ?? []).map((f) => ({
      fieldName: f.fieldName,
      confirmedValue: values[f.fieldName] ?? "",
    }));
    if (fields.length === 0) return;
    setBusyDocId(documentId);
    try {
      await ComplianceService.confirmFields(documentId, fields);
      toast.success("Fields saved");
      // Lock the fields after a confirmed save; Edit re-opens them.
      setFieldsEditing(false);
      await fetchAll();
    } catch (err) {
      console.error(err);
      toast.error(apiErrorMessageFormat(err, "Failed to save fields"));
    } finally {
      setBusyDocId(null);
    }
  };

  const setFieldValue = (documentId, fieldName, value) => {
    setFieldValues((prev) => ({
      ...prev,
      [documentId]: { ...(prev[documentId] ?? {}), [fieldName]: value },
    }));
    setFieldErrors((prev) => {
      if (!prev[fieldName]) return prev;
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  };

  if (loading) {
    return <p className="text-[13px] text-text-primary/60 py-10 text-center">Loading…</p>;
  }
  if (!candidate) {
    return <p className="text-[13px] text-text-primary/60 py-10 text-center">Candidate not found.</p>;
  }

  // The fleet-wide kill switch. `require_module_available_for_writes` refuses
  // every non-GET under /compliance with 503 while it is on, so every control
  // below that writes is disabled and every read is left exactly as it was:
  // the documents, the viewer, the extracted fields and the progress bar all
  // still work. `busy` covers one in-flight request; this covers the outage.
  const writesBlocked = !complianceAvailable;
  const busy = (selectedDoc && busyDocId === selectedDoc.id) || writesBlocked;
  // An upload runs AI extraction server-side, so it is slow enough that the
  // viewer must say something is happening rather than sitting still.
  const uploading = busyDocId === "upload";
  const extractedByName = {};
  (selectedDoc?.extractedFields ?? []).forEach((f) => {
    extractedByName[f.fieldName] = f;
  });
  const hasExtraction = (selectedDoc?.extractedFields?.length ?? 0) > 0;
  // Fields are read-only unless the officer is actively editing (and the doc
  // is not yet decided). `writesBlocked` too, for the one case the disabled
  // Edit button does not cover: the module going dark while an officer is
  // already mid-edit. Without it they keep typing into inputs whose Save is
  // disabled.
  // `awaitingCandidate` for the same reason `writesBlocked` is here: the
  // officer must not be left typing into inputs whose Save is gone.
  const fieldsLocked = decided || !fieldsEditing || writesBlocked || awaitingCandidate;
  // Approve must follow a confirmed save, not just AI extraction — an
  // officer who is still mid-edit (or never saved) hasn't actually verified
  // anything yet. Only applies when the checklist item has fields to save.
  const hasFieldsToSave = (selectedSub?.requiredFields?.length ?? 0) > 0;
  const fieldsUnsaved = !decided && hasFieldsToSave && fieldsEditing;

  return (
    <PermissionGuard permission={PERMISSIONS.COMPLIANCE_REVIEW}>
      <div className="flex flex-col gap-3" style={{ zoom: 0.9 }}>
        <ModuleUnavailableNotice
          module={MODULES.COMPLIANCE}
          suffix="Uploads and review decisions are paused. Every document, extracted field and progress figure below still reads normally."
        />

        {/* Hidden upload input */}
        <Input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={onFileSelected} />

        {/* Header */}
        <div className="p-3.5 sm:p-4 rounded-2xl bg-background-secondary border border-border-primary flex items-center gap-3 flex-wrap shadow-xs">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/admin/compliance/intake")}
            className="h-8 px-3.5 rounded-lg text-xs font-semibold border border-border-primary bg-background-primary text-text-primary hover:bg-background-tertiary/60"
          >
            <ArrowLeft size={14} /> Back
          </Button>
          <div className="min-w-0">
            <CandidateNameLink candidate={candidate} className="block text-[15px] font-semibold text-text-primary leading-tight" />
            <div className="text-[11px] text-text-primary/60 flex items-center gap-1 flex-wrap">
              {/* Hovering the address opens the contact card:
                                mail/dial links and copy buttons, so reaching a
                                candidate never means retyping an address. */}
              <CandidateContactPopover candidate={candidate}>
                <span className="cursor-default underline decoration-dotted underline-offset-2">{candidate.candidateEmail}</span>
              </CandidateContactPopover>
              {candidate.assignedChecklistName ? ` · Checklist: ${candidate.assignedChecklistName}` : ""}
              {currentSection?.name ? ` · Section: ${currentSection.name}` : ""}
            </div>
          </div>
          <div className="flex-1" />
          {/* The candidate's status already shows on the Intake row this
                        page was opened from, and the progress bar says the same
                        thing more precisely — one badge, not two. */}
          <div className="flex items-center gap-3">
            <div className="min-w-[200px]">
              <div className="flex items-center justify-between text-[11px] mb-1">
                <span className="text-text-primary/70">Weighted Progress</span>
                <span className="font-semibold text-text-primary">{progress?.percent ?? 0}%</span>
              </div>
              <div className="h-2 rounded-full bg-background-icon overflow-hidden">
                <div className="h-full bg-tone-emerald transition-all" style={{ width: `${progress?.percent ?? 0}%` }} />
              </div>
            </div>
            {/* Candidate-level history. Sits here rather than on each
                            document because the trail spans every document (and
                            the candidate's own portal uploads), so it is not a
                            property of whichever document happens to be open. */}
            <Button variant="outline" size="sm" className={cn(OUTLINE_BTN, ACTION_BTN)} onClick={() => setHistoryOpen(true)}>
              <Clock size={14} /> History
            </Button>
            {/* Candidate-level rejection (Phase 7C). Belongs beside
                            History for the same reason: it is a judgement about
                            the candidate, not about whichever document happens
                            to be open. Hidden once the case is already rejected
                            — re-rejecting says nothing new. */}
            {candidate?.status !== "rejected" && (
              <Button variant="destructive" size="sm" className={ACTION_BTN} onClick={() => setRejectOpen(true)}>
                <X size={14} /> Reject
              </Button>
            )}
            {/* Release (Phase C) — COMMENTED OUT, not removed. Kept for a
                            future release; the route, service, schema, modal and
                            `released_at` column all still exist and still work.
                            Only this control and the route decorator are off.

                            ⚠️ While it is off, a compliance run cannot be ended.
                            Release was the ONLY way to free a candidate whose run
                            finished (`active_run()` is `is_deleted = false AND
                            released_at IS NULL` — status is not consulted), so a
                            candidate sent to compliance on one job cannot be sent
                            on another: `send_from_recruitment` refuses with
                            COMPLIANCE_RUN_OWNED_BY_ANOTHER_APPLICATION. That is
                            accepted for now and is the thing to re-read before
                            turning this back on.

                            Original, restore verbatim:

            {!candidate?.releasedAt && (
              <Button variant="outline" size="sm" className={ACTION_BTN} onClick={() => setReleaseOpen(true)} disabled={writesBlocked}>
                <LogOut size={14} /> Release
              </Button>
            )}
                        */}
          </div>
        </div>

        <div className="space-y-3">
          {/* SECTION TABS */}
          {sections.length > 1 && (
            <div className="w-full flex items-center">
              <div className="inline-flex items-center gap-1 p-1 rounded-2xl border border-border-primary bg-background-secondary max-w-full overflow-x-auto custom-scrollbar">
                {sections.map((section) => {
                  const active = selectedSectionId === section.id;
                  const docCount = section.subsections?.length || 0;

                  return (
                    <Button
                      key={section.id}
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => changeSection(section.id)}
                      className={cn(
                        "h-7 px-3.5 rounded-xl text-xs transition-all whitespace-nowrap flex items-center gap-2 cursor-pointer",
                        active
                          ? "bg-button-primary text-text-invert shadow-sm font-semibold"
                          : "bg-transparent text-text-secondary hover:text-text-primary hover:bg-background-form/40 font-medium",
                      )}
                    >
                      <span>{section.name}</span>
                      <span
                        className={cn(
                          "text-[10px] font-bold h-4 min-w-[16px] px-1 rounded-full flex items-center justify-center",
                          active ? "bg-background-icon/30 text-text-invert" : "bg-background-form/80 text-text-secondary",
                        )}
                      >
                        {docCount}
                      </span>
                    </Button>
                  );
                })}
              </div>
            </div>
          )}

          {/* SUBSECTION TABS */}

          {currentSection && (
            <SectionTabs
              documents={currentSection.subsections.map((sub) => {
                const doc = docsBySub[sub.id];

                return {
                  id: sub.id,
                  label: sub.name,
                  isRequired: sub.isRequired ?? true,
                  status: (doc?.status || "NOT_UPLOADED").toUpperCase(),
                  progress: doc?.status === "approved" ? 100 : doc?.status === "re_requested" ? 50 : doc ? 25 : null,
                };
              })}
              activeDocument={selectedSubId}
              onChange={setSelectedSubId}
            />
          )}
        </div>

        {/* 3-section review workspace */}
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.8fr)_420px] gap-3 items-stretch">
          {/* ── SECTION 2: Document review / viewer ── */}
          <Card className="p-0 overflow-hidden flex flex-col h-[100vh]">
            <div className="shrink-0 px-3 py-2.5 border-b border-border-primary flex items-center gap-2 flex-wrap">
              {/* Long filenames are truncated, so the full name
                                lives in a tooltip rather than being unreachable. */}
              <CustomTooltip text={selectedDoc ? selectedDoc.originalFilename : ""}>
                <span className="text-[12px] font-semibold text-text-primary truncate max-w-[220px] block">
                  {selectedDoc ? selectedDoc.originalFilename : (selectedSub?.name ?? "Document")}
                </span>
              </CustomTooltip>
              <StatusBadge status={selectedDoc?.status} />
              <div className="flex-1" />
              {selectedDoc ? (
                // The per-document History button moved to the
                // header next to Weighted progress: history is
                // candidate-level, and a per-document trail lost
                // events whenever a document was replaced.
                <Button variant="outline" size="sm" className={cn(OUTLINE_BTN, ACTION_BTN)} disabled={!viewer.url} onClick={openInNewTab}>
                  <ExternalLink size={14} /> Open
                </Button>
              ) : selectedSub ? (
                <Button
                  variant="outline"
                  size="sm"
                  className={cn(OUTLINE_BTN, ACTION_BTN)}
                  disabled={uploading || writesBlocked}
                  onClick={() => triggerUpload(selectedSub.id, selectedSub.documentCategoryKey)}
                >
                  {uploading ? (
                    <>
                      <Loader2 size={14} className="animate-spin" /> Uploading…
                    </>
                  ) : (
                    <>
                      <Upload size={14} /> Upload
                    </>
                  )}
                </Button>
              ) : null}
            </div>
            <div className="flex-1 min-h-0 bg-background-icon/20 flex items-center justify-center">
              {uploading ? (
                <div className="flex flex-col items-center gap-2 px-6 text-center">
                  <Loader2 size={26} className="animate-spin text-button-primary" />
                  <p className="text-[12.5px] font-medium text-text-primary">Uploading document…</p>
                  <p className="text-[11px] text-text-primary/55">Running AI field extraction. Please wait.</p>
                </div>
              ) : !selectedDoc ? (
                <div className="text-center text-[12px] text-text-primary/50 px-6">
                  <FileText size={28} className="mx-auto mb-2 opacity-40" />
                  No document uploaded for this item yet.
                </div>
              ) : viewerLoading ? (
                <p className="text-[12px] text-text-primary/50">Loading document…</p>
              ) : !viewer.url ? (
                <p className="text-[12px] text-text-primary/50">Preview unavailable.</p>
              ) : isImage(selectedDoc.fileFormat) ? (
                <img src={viewer.url} alt={selectedDoc.originalFilename} className="max-h-full max-w-full object-contain" />
              ) : (
                <iframe title={selectedDoc.originalFilename} src={viewer.url} className="w-full h-full border-0" />
              )}
            </div>
          </Card>

          {/* ── SECTION 3: Extracted fields + review actions ── */}
          <Card className="p-0 overflow-hidden flex flex-col h-[100vh]">
            <div className="shrink-0 px-3 py-2.5 border-b border-border-primary flex items-center gap-1.5">
              <Sparkles size={14} className="text-tone-emerald" />
              <span className="text-[12px] font-semibold text-text-primary">Extracted Fields</span>
              {selectedDoc && (
                <span className="ml-auto text-[11px] text-text-primary/50">
                  {hasExtraction ? `${selectedDoc.extractedFields.length} field(s)` : selectedDoc.extractionAttempted ? "None Found" : "Not Run"}
                </span>
              )}
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto p-3 flex flex-col gap-3">
              {!selectedDoc ? (
                <p className="text-[12px] text-text-primary/50">Select an uploaded document to review its AI-extracted fields.</p>
              ) : (
                <>
                  {/* AI extraction summary line. No module
                                        gate of its own: extraction is part of
                                        Compliance now, and this whole route
                                        already requires it. It is also a read,
                                        so a fleet-wide kill must leave it
                                        exactly where it is — what a killed
                                        Compliance stops is the next upload and
                                        the next extraction, not the sentence
                                        describing the last one. */}
                  <div className="rounded-lg bg-tone-emerald/5 border border-tone-emerald/20 px-3 py-2">
                    <div className="flex items-center gap-1.5 text-[11px] text-text-primary/70">
                      <Sparkles size={12} className="text-tone-emerald" />
                      {hasExtraction ? (
                        <span>AI pre-filled these values — verify before approving.</span>
                      ) : selectedDoc.extractionAttempted ? (
                        <span>AI found no fields — enter values manually.</span>
                      ) : (
                        <span>Extraction has not run yet.</span>
                      )}
                    </div>
                  </div>

                  {/* Extracted / confirm field list */}
                  {(selectedSub?.requiredFields?.length ?? 0) === 0 ? (
                    <p className="text-[12px] text-text-primary/50">This checklist item has no configured fields to extract.</p>
                  ) : (
                    <div className="flex flex-col gap-2.5">
                      {selectedSub.requiredFields.map((f) => {
                        const ex = extractedByName[f.fieldName];
                        const value = fieldValues[selectedDoc.id]?.[f.fieldName] ?? "";

                        const multiline = f.fieldType === "textarea" || f.fieldType === "multiline" || f.fieldName.toLowerCase().includes("address") || value.length > 80;

                        const verify = needsVerification(ex);
                        const fieldError = fieldErrors[f.fieldName];

                        return (
                          <div
                            key={f.id ?? f.fieldName}
                            className={cn(
                              "rounded-lg border px-2.5 py-2",
                              fieldError ? "border-tone-rose/60 bg-tone-rose/5" : verify ? "border-tone-amber/60 bg-tone-amber/5" : "border-border-primary",
                            )}
                          >
                            <div className="flex items-center justify-between gap-2 mb-1">
                              <span className="text-[11px] font-medium text-text-primary/80">
                                {f.fieldName}
                                {f.isRequired !== false && (
                                  <span className="text-tone-rose ml-0.5" title="Required field">
                                    *
                                  </span>
                                )}
                              </span>
                              {ex ? <ConfidenceChip level={ex.confidenceLevel} /> : <span className="text-[9px] text-text-primary/40">Manual</span>}
                            </div>
                            {/* The officer signs off on these values, so
                                                            anything the model was unsure of is called
                                                            out rather than left to the chip alone. */}
                            {verify && (
                              <div className="mb-1.5 flex items-center gap-1 text-[10px] font-medium text-tone-amber">
                                <AlertTriangle size={10} />
                                Low AI confidence — please verify
                              </div>
                            )}
                            {fieldsLocked ? (
                              <div className="rounded-lg border border-border-primary bg-background-icon/20 px-3 py-2 min-h-[40px]">
                                <p className="text-[12px] text-text-primary whitespace-pre-wrap break-words">{value || "-"}</p>
                              </div>
                            ) : multiline ? (
                              <Textarea
                                rows={3}
                                readOnly={fieldsLocked}
                                value={value}
                                onChange={(e) => setFieldValue(selectedDoc.id, f.fieldName, e.target.value)}
                                placeholder={f.fieldType}
                                className={`w-full rounded-lg border bg-background-form/40 px-3 py-2 text-[12px] text-text-primary resize-y outline-none transition ${
                                  fieldError ? "border-tone-rose" : "border-border-primary"
                                } ${fieldsLocked ? "opacity-70 cursor-not-allowed resize-none" : ""}`}
                              />
                            ) : (
                              <Input
                                readOnly={fieldsLocked}
                                value={value}
                                onChange={(e) => setFieldValue(selectedDoc.id, f.fieldName, e.target.value)}
                                placeholder={f.fieldType}
                                className={cn(fieldError && "border-tone-rose focus-visible:ring-tone-rose", fieldsLocked && "opacity-70 cursor-not-allowed")}
                              />
                            )}
                            {fieldError && !fieldsLocked && <p className="mt-1 text-[10.5px] text-tone-rose">{fieldError}</p>}
                            {ex?.rawSnippet && (
                              <div className="mt-1 text-[10px] text-text-primary/45 whitespace-normal break-words">
                                <span className="opacity-60">Source:</span> {ex.rawSnippet}
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {/* Save fields (with confirmation) while editing; Edit to
                                                re-open once saved. Both disappear once the document is
                                                approved/rejected — the fields become permanently locked. */}
                      {!decided && !awaitingCandidate && (
                        <div className="flex items-center gap-2">
                          {fieldsEditing ? (
                            <Button variant="secondary" size="sm" className={cn(SECONDARY_BTN, "rounded-lg")} disabled={busy} onClick={handleSaveFieldsClick}>
                              <Check size={14} /> Save Fields
                            </Button>
                          ) : (
                            <Button variant="outline" size="sm" className={cn(OUTLINE_BTN, "rounded-lg")} disabled={busy} onClick={() => setFieldsEditing(true)}>
                              <Pen size={14} /> Edit Fields
                            </Button>
                          )}
                        </div>
                      )}
                      {decided && (
                        <div className="flex items-center gap-1.5 text-[10.5px] text-text-primary/50">
                          <Lock size={12} />
                          Fields are locked — this document has been {selectedDoc.status === "approved" ? "approved" : "rejected"}.
                        </div>
                      )}
                      {awaitingCandidate && !decided && (
                        <div className="flex items-center gap-1.5 text-[10.5px] text-text-primary/50">
                          <Clock size={12} />
                          {AWAITING_CANDIDATE_MESSAGE}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Review actions */}
                  <div className="mt-1 pt-3 border-t border-border-primary flex flex-col gap-2">
                    <span className="text-[11px] text-text-primary/60">Review Decision</span>
                    <div className="flex items-center gap-2 flex-wrap">
                      {/* Approve is a final decision — disabled once the
                                                document is already approved/rejected. Re-request always
                                                stays available: it's the escape hatch for a mistaken
                                                decision and for asking the candidate to resend a doc
                                                (extraction re-runs on the new upload).

                                                Reject is NOT here any more (Phase 7C). A document
                                                problem is a re-request; rejecting is a judgement about
                                                the candidate, so it lives in the header. Rejecting a
                                                single document left the case in limbo with no way to
                                                conclude it. */}
                      <CustomTooltip text={awaitingCandidate ? AWAITING_CANDIDATE_MESSAGE : fieldsUnsaved ? "Save the extracted fields before approving." : ""}>
                        <Button
                          variant="default"
                          size="sm"
                          className={cn(PRIMARY_BTN, ACTION_BTN)}
                          disabled={busy || decided || fieldsUnsaved || awaitingCandidate}
                          onClick={() => setApproveConfirmOpen(true)}
                        >
                          <Check size={14} /> Approve
                        </Button>
                      </CustomTooltip>
                      {/* A second re-request would open a second log row that
                          the re-upload never closes, so it waits for the
                          replacement like every other write. */}
                      <CustomTooltip text={awaitingCandidate ? AWAITING_CANDIDATE_MESSAGE : ""}>
                        <Button
                          variant="outline"
                          size="sm"
                          className={cn(OUTLINE_BTN, ACTION_BTN)}
                          disabled={busy || awaitingCandidate}
                          onClick={() =>
                            setReason({
                              open: true,
                              mode: "rerequest",
                              documentId: selectedDoc.id,
                              text: "",
                            })
                          }
                        >
                          <RotateCcw size={14} /> Re-request
                        </Button>
                      </CustomTooltip>
                    </div>
                    {fieldsUnsaved && !awaitingCandidate && (
                      <p className="text-[10px] text-tone-rose">Save the extracted fields before you can approve this document.</p>
                    )}
                    {awaitingCandidate && <p className="text-[10px] text-text-primary/50">{AWAITING_CANDIDATE_MESSAGE}</p>}
                    {selectedDoc.rerequestDescription && selectedDoc.status === "re_requested" && (
                      <p className="text-[10px] text-text-primary/50">Re-requested: {selectedDoc.rerequestDescription}</p>
                    )}
                  </div>
                </>
              )}
            </div>
          </Card>
        </div>

        {/* Reason modal (reject / re-request) */}
        <Dialog open={reason.open} onOpenChange={(o) => !o && setReason({ open: false, mode: null, documentId: null, text: "" })}>
          <DialogContent className="w-[460px] md:w-full flex flex-col p-0">
            <DialogHeader className="relative px-5 py-3 border-b border-border-primary">
              <DialogTitle className="text-[15px] font-semibold text-text-primary">{reason.mode === "reject" ? "Reject candidate" : "Re-request document"}</DialogTitle>
              <DialogClose className="cursor-pointer absolute right-5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-background-icon/40 flex items-center justify-center">
                <X size={16} strokeWidth={3} />
              </DialogClose>
            </DialogHeader>
            <div className="px-5 py-4" style={{ zoom: 0.9 }}>
              <label className="block text-[12px] font-medium mb-1">{reason.mode === "reject" ? "Reason (optional)" : "What should the candidate fix?"}</label>

              {/* Quick tags fill the box with the reasons officers
                                write most often; the text stays editable after. */}
              <div className="flex flex-wrap gap-1.5 mb-2">
                {PRESET_REASON_TAGS.map((tag) => (
                  <Button
                    key={tag}
                    type="button"
                    onClick={() => setReason((r) => ({ ...r, text: tag }))}
                    className="px-2 py-1 rounded-full border border-border-primary bg-background-form/40 text-[11px] text-text-primary/75 hover:border-button-primary/40 hover:text-text-primary transition-colors"
                  >
                    + {tag}
                  </Button>
                ))}
              </div>

              <Textarea
                rows={4}
                value={reason.text}
                onChange={(e) => setReason((r) => ({ ...r, text: e.target.value }))}
                className="w-full rounded-lg bg-background-form/40 border border-border-primary px-3 py-2 text-[13px] text-text-primary outline-none"
                placeholder={
                  reason.mode === "reject"
                    ? "Add an optional note explaining why candidate was rejected…"
                    : "e.g. The passport scan is blurry — please resend a clear copy."
                }
              />
              <div className="flex justify-end gap-3 mt-4">
                <DialogClose
                  render={
                    <Button variant="outline" className={`${OUTLINE_BTN} border-none`}>
                      Cancel
                    </Button>
                  }
                />
                {/* Gated on the document the MODAL names, not the selected
                    one: the two can differ, and this button is the second way
                    into a re-request. Without it a stale open dialog sends the
                    duplicate the trigger button refuses. */}
                <Button
                  variant={reason.mode === "reject" ? "destructive" : "secondary"}
                  className={reason.mode === "reject" ? "" : SECONDARY_BTN}
                  onClick={submitReason}
                  disabled={busyDocId === reason.documentId || writesBlocked || reasonDocAwaitingCandidate}
                >
                  {reason.mode === "reject" ? "Reject Candidate" : "Send re-request"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Save-fields confirmation — the officer attests the values are verified. */}
        <AlertModal
          open={saveFieldsConfirmOpen}
          title="Confirm extracted field values?"
          description="Please confirm you have reviewed and verified these values against the document. You can edit them again afterwards if needed."
          actionText="Yes, save fields"
          cancelText="Cancel"
          onOpenChange={(o) => !o && setSaveFieldsConfirmOpen(false)}
          onAction={() => {
            setSaveFieldsConfirmOpen(false);
            if (selectedDoc && selectedSub) saveFields(selectedDoc.id, selectedSub.requiredFields);
          }}
          onCancel={() => setSaveFieldsConfirmOpen(false)}
          actionClassName="bg-button-secondary text-text-base"
        />

        {/* Candidate audit history — every document plus the candidate's
                    own portal uploads, in one attributed timeline. */}
        {/* Upload attestation (Phase 8C). Placeholder copy — the exact
                sentence lives in one constant and is persisted server-side as
                shown, so a later reword does not rewrite past consents. */}
        {attestation && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-[480px] rounded-2xl bg-background-primary border border-border-primary shadow-xl">
              <div className="px-5 py-3 border-b border-border-primary">
                <h2 className="text-[15px] font-semibold text-text-primary">Confirm before uploading</h2>
              </div>
              <div className="p-5">
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <input type="checkbox" className="mt-0.5" checked={attestationChecked} onChange={(e) => setAttestationChecked(e.target.checked)} />
                  <span className="text-[12.5px] text-text-primary/85">{COMPLIANCE_OFFICER_UPLOAD_ATTESTATION}</span>
                </label>
                <p className="text-[11px] text-text-primary/55 mt-2.5">Your acceptance is recorded against this document.</p>
              </div>
              <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border-primary">
                <Button variant="outline" size="sm" onClick={() => setAttestation(null)}>
                  Cancel
                </Button>
                <Button variant="default" size="sm" className={PRIMARY_BTN} disabled={!attestationChecked} onClick={confirmAttestation}>
                  Accept and choose file
                </Button>
              </div>
            </div>
          </div>
        )}

        <RejectCandidateModal
          open={rejectOpen}
          onClose={() => setRejectOpen(false)}
          complianceCandidateId={id}
          candidateName={candidate?.candidateName}
          documents={rejectableDocuments}
          onRejected={fetchAll}
        />

        {/* Release modal — COMMENTED OUT with its button above. The component
            file, the service call and the route all still exist; nothing was
            deleted. `releaseOpen`/`setReleaseOpen` and the import are kept so
            restoring is uncommenting these two blocks and nothing else.

        <ReleaseCandidateModal
          open={releaseOpen}
          onClose={() => setReleaseOpen(false)}
          complianceCandidateId={id}
          candidateName={candidate?.candidateName}
          owningJobTitle={candidate?.owningJobTitle}
          // Back to Intake, not `fetchAll`. A released run leaves every live
          // read path, so refetching this page would reload a record the
          // officer can no longer act on — the detail screen is entirely
          // review controls for a review that has ended. Intake rather than
          // the dashboard because it is where the officer came from (the Back
          // button above goes there too), and the released run is visibly gone
          // from that list, which is its own confirmation.
          onReleased={() => navigate("/admin/compliance/intake")}
        />
        */}

        <CandidateAuditLogModal open={historyOpen} complianceCandidateId={id} candidateName={candidate?.candidateName} onClose={() => setHistoryOpen(false)} />

        {/* Approve confirmation — approving locks the fields permanently. */}
        <AlertModal
          open={approveConfirmOpen}
          title="Approve this document?"
          description="Once approved, the extracted fields are locked and can no longer be edited. If you need the candidate to resend a corrected document, use Re-request instead."
          actionText="Yes, approve"
          cancelText="Cancel"
          onOpenChange={(o) => !o && setApproveConfirmOpen(false)}
          onAction={() => {
            setApproveConfirmOpen(false);
            if (selectedDoc) approve(selectedDoc.id);
          }}
          onCancel={() => setApproveConfirmOpen(false)}
          actionClassName="bg-button-primary text-text-invert"
        />
      </div>
    </PermissionGuard>
  );
};

export default ComplianceDetail;
